// src/components/Common/CookieConsent.jsx
import React, { useState, useEffect } from "react";
import { FiX, FiSettings, FiCheck, FiShield, FiInfo } from "react-icons/fi";
import { Link } from "react-router-dom";
import { persistMarketingConsent } from "../../utils/marketingTracking";
import { isAdminStorefrontPreview } from "../../utils/storefrontPreview";
import "../../styles/cookie-consent.css";

const CookieConsent = () => {
  const isAdminPreview = isAdminStorefrontPreview();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (isAdminPreview) return undefined;
    // Check if user has already consented
    const hasConsented = localStorage.getItem("cookieConsent");
    const savedPreferences = localStorage.getItem("cookiePreferences");

    if (hasConsented === "true" && savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(parsed);
      } catch (e) {
        console.error("Error parsing cookie preferences");
      }
      setShowBanner(false);
    } else {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAdminPreview]);

  const saveConsent = async (chosenPreferences) => {
    const normalized = {
      necessary: true,
      functional: chosenPreferences.functional === true,
      analytics: chosenPreferences.analytics === true,
      marketing: chosenPreferences.marketing === true,
    };
    setPreferences(normalized);
    localStorage.setItem("cookieConsent", "true");
    localStorage.setItem("cookiePreferences", JSON.stringify(normalized));
    setShowBanner(false);
    setShowSettings(false);
    try {
      await persistMarketingConsent(normalized);
    } catch (error) {
      console.warn("Could not persist cookie consent to the server:", error);
    }
    window.dispatchEvent(new Event("cookieConsentUpdated"));
  };

  const acceptAll = () =>
    saveConsent({
      functional: true,
      analytics: true,
      marketing: true,
    });

  const declineAll = () =>
    saveConsent({
      functional: false,
      analytics: false,
      marketing: false,
    });

  const savePreferences = () => saveConsent(preferences);

  const updatePreference = (type, value) => {
    if (type === "necessary") return; // Cannot change necessary
    setPreferences((prev) => ({ ...prev, [type]: value }));
  };

  if (isAdminPreview) return null;

  if (!showBanner) {
    return (
      <button
        type="button"
        className="cookie-preferences-trigger"
        onClick={() => {
          setShowBanner(true);
          setShowSettings(true);
        }}
        aria-label="Open cookie preferences"
      >
        <FiSettings /> Cookie settings
      </button>
    );
  }

  return (
    <>
      {/* Cookie Banner */}
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <div className="cookie-icon">
            <FiShield />
          </div>
          <div className="cookie-text">
            <h3>🍪 We Value Your Privacy</h3>
            <p>
              We use cookies to enhance your browsing experience, serve
              personalized content, and analyze our traffic. By clicking "Accept
              All", you consent to our use of cookies.
            </p>
            <div className="cookie-links">
              <Link to="/privacy" onClick={() => setShowBanner(false)}>
                Privacy Policy
              </Link>
              <span className="separator">•</span>
              <Link to="/cookies" onClick={() => setShowBanner(false)}>
                Cookie Policy
              </Link>
            </div>
          </div>
          <div className="cookie-buttons">
            <button
              className="cookie-btn-settings"
              onClick={() => setShowSettings(true)}
            >
              <FiSettings /> Customize
            </button>
            <button className="cookie-btn-decline" onClick={declineAll}>
              Decline
            </button>
            <button className="cookie-btn-accept" onClick={acceptAll}>
              <FiCheck /> Accept All
            </button>
          </div>
          <button className="cookie-close" onClick={declineAll}>
            <FiX />
          </button>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div
          className="cookie-modal-overlay"
          onClick={() => setShowSettings(false)}
        >
          <div className="cookie-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cookie-modal-header">
              <h2>
                <FiShield /> Cookie Preferences
              </h2>
              <button
                className="cookie-modal-close"
                onClick={() => setShowSettings(false)}
              >
                <FiX />
              </button>
            </div>
            <div className="cookie-modal-body">
              <p className="cookie-modal-description">
                We use cookies to help you navigate efficiently and perform
                certain functions. You'll find detailed information about all
                cookies under each consent category below.
              </p>

              {/* Necessary Cookies - Always On */}
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <div>
                    <h3>✅ Necessary Cookies</h3>
                    <p>Always Active</p>
                  </div>
                  <div className="cookie-toggle disabled">
                    <span className="toggle-on">On</span>
                  </div>
                </div>
                <div className="cookie-option-details">
                  Necessary cookies are required to enable the basic features of
                  this site, such as providing secure log-in or remembering your
                  shopping cart.
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <div>
                    <h3>⚙️ Functional Cookies</h3>
                    <p>Enhance site functionality</p>
                  </div>
                  <label className="cookie-toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) =>
                        updatePreference("functional", e.target.checked)
                      }
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
                <div className="cookie-option-details">
                  These cookies allow the website to remember choices you make
                  (such as your language, region, or theme) and provide
                  enhanced, more personal features.
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <div>
                    <h3>📊 Analytics Cookies</h3>
                    <p>Help us improve our website</p>
                  </div>
                  <label className="cookie-toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        updatePreference("analytics", e.target.checked)
                      }
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
                <div className="cookie-option-details">
                  These cookies help us understand how visitors interact with
                  our website by collecting and reporting information
                  anonymously. This helps us improve our site.
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <div>
                    <h3>🎯 Marketing Cookies</h3>
                    <p>Personalized advertising</p>
                  </div>
                  <label className="cookie-toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        updatePreference("marketing", e.target.checked)
                      }
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
                <div className="cookie-option-details">
                  These cookies track your online activity to help advertisers
                  deliver more relevant advertising or to limit how many times
                  you see an ad.
                </div>
              </div>
            </div>
            <div className="cookie-modal-footer">
              <button className="cookie-modal-decline" onClick={declineAll}>
                Decline All
              </button>
              <button className="cookie-modal-save" onClick={savePreferences}>
                Save Preferences
              </button>
              <button className="cookie-modal-accept" onClick={acceptAll}>
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
